/**
 * Asset Library body — left nav (Templates / Library Assets groups), a
 * header with a close button, and a shared footer with a per-tab action.
 *
 * Only "Slide Templates" and "Brand Templates" have real panels; every other
 * tab renders the shared "coming soon" placeholder. Neither real tab
 * inserts/applies anything itself: Slide Templates hands off to
 * PowerPoint.run() via the task pane, and Brand Templates only routes into
 * Apply Brand's Saved Templates tab with a pick pre-selected. That routing
 * runs the other way too — Apply Brand's own Saved Templates tab can open
 * THIS component straight onto a template's detail view (see
 * brandTemplatePreviewHandoff.ts), which is why the default tab is decided
 * at mount rather than hardcoded.
 *
 * The footer is shared chrome, but its label/button are entirely per-tab
 * (see types.ts's AssetLibraryFooterState) — Slide Templates reports
 * selection count + "Insert into deck" via onSelectionStateChange + an
 * imperative ref; Brand Templates reports its own label via
 * onFooterStateChange. Every other tab falls back to
 * DEFAULT_ASSET_LIBRARY_FOOTER_STATE.
 *
 * No outer scrim/overlay of its own — mounted either inline behind a
 * `<dialog>` backdrop (no Office Dialog API) or filling the Office Dialog
 * window's own card (AssetLibraryDialogApp.tsx).
 */

import { useCallback, useRef, useState, type RefObject } from "react";
import {
  SlideTemplatesPanel,
  type SlideTemplatesPanelHandle,
  type SlideTemplatesSelectionState,
} from "../../slide-templates/components/SlideTemplatesPanel";
import type { UseSlideTemplatesOptions } from "../../slide-templates/hooks/useSlideTemplates";
import { logSlideTemplatesError } from "../../slide-templates/services/slideTemplatesApi";
import {
  PresentationTemplatesPanel,
  type PresentationTemplatesPanelHandle,
  type PresentationTemplatesSelectionState,
} from "../../presentation-templates/components/PresentationTemplatesPanel";
import type { UsePresentationTemplatesOptions } from "../../presentation-templates/hooks/usePresentationTemplates";
import { logPresentationTemplatesError } from "../../presentation-templates/services/presentationTemplatesApi";
import { getErrorMessage } from "../../shared/services/connectorErrors";
import { BrandTemplatesTab } from "./BrandTemplatesTab";
import type { DialogBrandTemplates } from "../hooks/useDialogBrandTemplates";
import { DEFAULT_ASSET_LIBRARY_FOOTER_STATE, type AssetLibraryFooterState } from "../types";
import { hasPendingBrandTemplatePreview } from "../services/brandTemplatePreviewHandoff";
import styles from "./AssetLibraryDialog.module.css";

type LibraryTab =
  | "brand-templates"
  | "slide-templates"
  | "presentation-templates"
  | "icons"
  | "logos";

interface TabSpec {
  id: LibraryTab;
  label: string;
}

const TEMPLATE_TABS: TabSpec[] = [
  { id: "brand-templates", label: "Brand Templates" },
  { id: "slide-templates", label: "Slide Templates" },
  { id: "presentation-templates", label: "Presentation Templates" },
];

const LIBRARY_ASSET_TABS: TabSpec[] = [
  { id: "icons", label: "Icons" },
  { id: "logos", label: "Logos / Brands" },
];

interface AssetLibraryContentProps {
  readonly onClose: () => void;
  /**
   * Forwarded to SlideTemplatesPanel. Omitted for the in-pane mount (which
   * fetches and inserts directly); AssetLibraryDialogApp supplies the
   * hand-off/messageParent pair, since an Office Dialog has neither a bearer
   * token nor PowerPoint.run().
   */
  readonly slideTemplatesOptions?: UseSlideTemplatesOptions;
  /**
   * Forwarded to BrandTemplatesTab. Omitted for the in-pane mount (which
   * fetches directly); AssetLibraryDialogApp supplies its useDialogBrandTemplates()
   * result, since an Office Dialog has no bearer token for the org fetch.
   */
  readonly brandTemplates?: DialogBrandTemplates;
  /**
   * Forwarded to PresentationTemplatesPanel. Omitted for the in-pane mount
   * (which fetches and inserts directly); AssetLibraryDialogApp supplies the
   * hand-off/messageParent pair, since an Office Dialog has neither a bearer
   * token nor PowerPoint.run().
   */
  readonly presentationTemplatesOptions?: UsePresentationTemplatesOptions;
}

const DEFAULT_SLIDE_TEMPLATES_SELECTION: SlideTemplatesSelectionState = {
  count: 0,
  canInsert: false,
  isInserting: false,
};

const DEFAULT_PRESENTATION_TEMPLATES_SELECTION: PresentationTemplatesSelectionState = {
  count: 0,
  canInsert: false,
  isInserting: false,
};

export function AssetLibraryContent({
  onClose,
  slideTemplatesOptions,
  brandTemplates,
  presentationTemplatesOptions,
}: Readonly<AssetLibraryContentProps>) {
  // A staged preview (Apply Brand's Saved Templates eye icon — see
  // brandTemplatePreviewHandoff.ts) means this open should land straight on
  // Brand Templates, not the usual Slide Templates default.
  const [activeTab, setActiveTab] = useState<LibraryTab>(() =>
    hasPendingBrandTemplatePreview() ? "brand-templates" : "slide-templates",
  );
  const [slideTemplatesSelection, setSlideTemplatesSelection] = useState<SlideTemplatesSelectionState>(
    DEFAULT_SLIDE_TEMPLATES_SELECTION,
  );
  const slideTemplatesRef = useRef<SlideTemplatesPanelHandle>(null);
  const [brandFooterState, setBrandFooterState] = useState<AssetLibraryFooterState | null>(null);
  const [presentationTemplatesSelection, setPresentationTemplatesSelection] = useState<PresentationTemplatesSelectionState>(
    DEFAULT_PRESENTATION_TEMPLATES_SELECTION,
  );
  const presentationTemplatesRef = useRef<PresentationTemplatesPanelHandle>(null);

  const isSlideTemplatesTab = activeTab === "slide-templates";
  const isBrandTemplatesTab = activeTab === "brand-templates";
  const isPresentationTemplatesTab = activeTab === "presentation-templates";

  const handleInsert = useCallback(() => {
    // Failures also surface inside SlideTemplatesPanel's own insertError
    // state — this catch only exists so the rejection is traced, not swallowed.
    slideTemplatesRef.current?.insertSelected().catch((error: unknown) => {
      logSlideTemplatesError("asset-library-insert-selected-failed", getErrorMessage(error, "Insert into deck failed"));
    });
  }, []);

  const handlePresentationInsert = useCallback(() => {
    // Failures also surface inside PresentationTemplatesPanel's own
    // insertError state — this catch only exists so the rejection is traced,
    // not swallowed.
    presentationTemplatesRef.current?.insertSelected().catch((error: unknown) => {
      logPresentationTemplatesError(
        "asset-library-presentation-insert-selected-failed",
        getErrorMessage(error, "Insert into deck failed"),
      );
    });
  }, []);

  let footerState: AssetLibraryFooterState;
  if (isSlideTemplatesTab) {
    footerState = {
      selectionLabel: `${slideTemplatesSelection.count} selected`,
      actionLabel: slideTemplatesSelection.isInserting ? "Inserting…" : "Insert into deck",
      actionDisabled: !slideTemplatesSelection.canInsert,
      onAction: handleInsert,
    };
  } else if (isBrandTemplatesTab && brandFooterState) {
    footerState = brandFooterState;
  } else if (isPresentationTemplatesTab) {
    footerState = {
      selectionLabel: `${presentationTemplatesSelection.count} selected`,
      actionLabel: presentationTemplatesSelection.isInserting ? "Inserting…" : "Insert into deck",
      actionDisabled: !presentationTemplatesSelection.canInsert,
      onAction: handlePresentationInsert,
    };
  } else {
    footerState = DEFAULT_ASSET_LIBRARY_FOOTER_STATE;
  }

  return (
    <>
      <div className={styles.header}>
        <span className={styles.headerTitle}>Asset Library</span>
        <button type="button" className={styles.closeBtn} aria-label="Close Asset Library" onClick={onClose}>
          ×
        </button>
      </div>

      <div className={styles.body}>
        <nav className={styles.nav} aria-label="Asset Library sections">
          <NavGroup title="TEMPLATES" tabs={TEMPLATE_TABS} activeTab={activeTab} onSelect={setActiveTab} />
          <NavGroup title="LIBRARY ASSETS" tabs={LIBRARY_ASSET_TABS} activeTab={activeTab} onSelect={setActiveTab} />
        </nav>

        <div className={styles.content}>
          <ActiveTabPanel
            isSlideTemplatesTab={isSlideTemplatesTab}
            isBrandTemplatesTab={isBrandTemplatesTab}
            isPresentationTemplatesTab={isPresentationTemplatesTab}
            activeTab={activeTab}
            onClose={onClose}
            slideTemplatesRef={slideTemplatesRef}
            slideTemplatesOptions={slideTemplatesOptions}
            onSlideTemplatesSelectionChange={setSlideTemplatesSelection}
            brandTemplates={brandTemplates}
            onBrandFooterStateChange={setBrandFooterState}
            presentationTemplatesRef={presentationTemplatesRef}
            presentationTemplatesOptions={presentationTemplatesOptions}
            onPresentationTemplatesSelectionChange={setPresentationTemplatesSelection}
          />
        </div>
      </div>

      <div className={styles.footer}>
        <span className={styles.selectionCount}>{footerState.selectionLabel}</span>
        <button
          type="button"
          className={styles.insertBtn}
          disabled={footerState.actionDisabled}
          onClick={footerState.onAction}
          title={footerState.actionLabel}
        >
          {footerState.actionLabel}
        </button>
      </div>
    </>
  );
}

interface ActiveTabPanelProps {
  isSlideTemplatesTab: boolean;
  isBrandTemplatesTab: boolean;
  isPresentationTemplatesTab: boolean;
  activeTab: LibraryTab;
  onClose: () => void;
  slideTemplatesRef: RefObject<SlideTemplatesPanelHandle>;
  slideTemplatesOptions: UseSlideTemplatesOptions | undefined;
  onSlideTemplatesSelectionChange: (state: SlideTemplatesSelectionState) => void;
  brandTemplates: DialogBrandTemplates | undefined;
  onBrandFooterStateChange: (state: AssetLibraryFooterState | null) => void;
  presentationTemplatesRef: RefObject<PresentationTemplatesPanelHandle>;
  presentationTemplatesOptions: UsePresentationTemplatesOptions | undefined;
  onPresentationTemplatesSelectionChange: (state: PresentationTemplatesSelectionState) => void;
}

/** Only "Slide Templates", "Brand Templates", and "Presentation Templates"
 *  have real panels — every other tab falls back to the shared "coming soon"
 *  placeholder. */
function ActiveTabPanel({
  isSlideTemplatesTab, isBrandTemplatesTab, isPresentationTemplatesTab, activeTab, onClose, slideTemplatesRef,
  slideTemplatesOptions, onSlideTemplatesSelectionChange, brandTemplates, onBrandFooterStateChange,
  presentationTemplatesRef, presentationTemplatesOptions, onPresentationTemplatesSelectionChange,
}: Readonly<ActiveTabPanelProps>) {
  if (isSlideTemplatesTab) {
    return (
      <SlideTemplatesPanel
        ref={slideTemplatesRef}
        onSelectionStateChange={onSlideTemplatesSelectionChange}
        templatesOptions={slideTemplatesOptions}
      />
    );
  }
  if (isBrandTemplatesTab) {
    return <BrandTemplatesTab onClose={onClose} dialogTemplates={brandTemplates} onFooterStateChange={onBrandFooterStateChange} />;
  }
  if (isPresentationTemplatesTab) {
    return (
      <PresentationTemplatesPanel
        ref={presentationTemplatesRef}
        onSelectionStateChange={onPresentationTemplatesSelectionChange}
        templatesOptions={presentationTemplatesOptions}
      />
    );
  }
  // isPresentationTemplatesTab is false here, but it's derived from activeTab
  // in the parent rather than narrowed on this variable directly — an assert
  // (not a widening cast) keeps ComingSoon's prop type honestly excluding the
  // one tab that now always has a real panel above.
  return <ComingSoon tab={activeTab as Exclude<LibraryTab, "presentation-templates">} />;
}

function NavGroup({
  title,
  tabs,
  activeTab,
  onSelect,
}: Readonly<{
  title: string;
  tabs: TabSpec[];
  activeTab: LibraryTab;
  onSelect: (tab: LibraryTab) => void;
}>) {
  return (
    <div className={styles.navGroup}>
      <div className={styles.navGroupLabel}>{title}</div>
      <ul className={styles.navList}>
        {tabs.map((tab) => (
          <li key={tab.id}>
            <button
              type="button"
              className={`${styles.navItem} ${activeTab === tab.id ? styles.navItemActive : ""}`}
              aria-pressed={activeTab === tab.id}
              onClick={() => onSelect(tab.id)}
            >
              {tab.label}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

const COMING_SOON_COPY: Record<Exclude<LibraryTab, "presentation-templates">, string> = {
  "brand-templates": "Brand templates are coming soon.",
  "slide-templates": "Slide templates are coming soon.",
  icons: "The icon library is coming soon.",
  logos: "Logos & brand assets are coming soon.",
};

function ComingSoon({ tab }: Readonly<{ tab: Exclude<LibraryTab, "presentation-templates"> }>) {
  return (
    <div className={styles.comingSoon}>
      <span className={styles.comingSoonIcon} aria-hidden="true">🗂</span>
      <span className={styles.comingSoonText}>{COMING_SOON_COPY[tab]}</span>
    </div>
  );
}
